/**
 * Module 'finance': the finance and cost portal (docs/product/CostTrackingMobileControl.md
 * sections 2 source C, 3 other project costs, 5 and 10). It extends module 'costs': token
 * spend comes from the costs ledger, everything else (seats, compute, CI, platform invoices,
 * manual entries with receipts) is kept here, in the same daily ledger, so a project's P&L
 * adds up every category. Billing truth is pulled from the provider's usage and cost report
 * and reconciled against the transcript ledger by day, model and key.
 */
import type { Id, Ts } from '../index.ts';
import type { AccountBucket } from './accounts.ts';

export type CostCategory = 'tokens' | 'seats' | 'compute' | 'ci' | 'platform' | 'other';
export const COST_CATEGORIES: readonly CostCategory[] = ['tokens', 'seats', 'compute', 'ci', 'platform', 'other'];
export const COST_CATEGORY_LABEL: Record<CostCategory, string> = {
  tokens: 'Tokens',
  seats: 'Seats and subscriptions',
  compute: 'Node compute',
  ci: 'CI minutes',
  platform: 'Platform',
  other: 'Other',
};

/** Categories a person (or an import) can add entries to. Tokens only come from the costs ledger. */
export type EntryCategory = Exclude<CostCategory, 'tokens'>;
export const ENTRY_CATEGORIES: readonly EntryCategory[] = ['seats', 'compute', 'ci', 'platform', 'other'];

/** Where an entry came from. 'github' entries are replaced on every CI pull. */
export type EntrySource = 'manual' | 'invoice' | 'github';

export interface Receipt {
  name: string;
  /** 'application/pdf', 'image/png', 'image/jpeg' or 'image/webp'. */
  type: string;
  size: number;
  uploadedAt: Ts;
}

/**
 * A cost that is not tokens: a one-off spend or an invoice. The amount is spread evenly over
 * the days from `from` to `to` (inclusive, local days 'YYYY-MM-DD'); a one-off has from === to.
 */
export interface CostEntry {
  id: Id;
  category: EntryCategory;
  /** 'Cloudflare', 'GitHub', 'Hetzner'. */
  vendor: string;
  description: string;
  /** USD for the whole range. */
  amount: number;
  from: string;
  to: string;
  /** null: shared, split across projects by their token spend in the same week. */
  projectId: Id | null;
  source: EntrySource;
  invoiceNumber?: string;
  receipt?: Receipt;
  /** CI pulls: billed minutes behind the amount. */
  minutes?: number;
  createdBy: string;
  createdAt: Ts;
  updatedAt: Ts;
}

/** POST /api/finance/entries and PUT /api/finance/entries/:id. */
export interface CostEntryInput {
  category: EntryCategory;
  vendor: string;
  description: string;
  amount: number;
  from: string;
  to?: string;
  projectId?: Id | null;
  source?: 'manual' | 'invoice';
  invoiceNumber?: string;
}

/**
 * A seat or subscription that renews every month or year. Amortised per day: a monthly
 * charge is divided by the days of each calendar month, a yearly one by the days of the year.
 */
export interface Subscription {
  id: Id;
  vendor: string;
  /** 'Claude Max', 'GitHub Team', 'Linear Standard'. */
  plan: string;
  seats: number;
  /** USD per seat per period. */
  pricePerSeat: number;
  every: 'month' | 'year';
  /** First day billed, and the last (null while it runs). */
  start: string;
  end: string | null;
  projectId: Id | null;
  /** True for the agent seats themselves (Claude Pro, Max, Team). */
  agentSeat: boolean;
  createdAt: Ts;
  updatedAt: Ts;
}

export interface SubscriptionInput {
  vendor: string;
  plan: string;
  seats: number;
  pricePerSeat: number;
  every: 'month' | 'year';
  start: string;
  end?: string | null;
  projectId?: Id | null;
  agentSeat?: boolean;
}

/** An hourly rate for an owned machine: compute = hours online times the rate. */
export interface ComputeRate {
  machineId: Id;
  /** USD per hour online. */
  hourlyRate: number;
}

/** An Anthropic API key id (from the usage report) and which machines use it. */
export interface KeyMapping {
  /** 'apikey_01Rj2N8S...'. */
  keyId: string;
  label: string;
  machineIds: Id[];
}

/** A GitHub repository whose CI minutes and pull requests belong to a project. */
export interface RepoMapping {
  /** 'owner/name'. */
  repo: string;
  projectId: Id;
}

/** Chart of accounts for the accounting export. */
export type AccountMap = Record<CostCategory, string>;
export const DEFAULT_ACCOUNTS: AccountMap = {
  tokens: '6110 AI usage',
  seats: '6120 Software subscriptions',
  compute: '6130 Hosting and compute',
  ci: '6140 CI and build minutes',
  platform: '6150 Platform services',
  other: '6190 Other development costs',
};

export interface FinanceSettings {
  computeRates: ComputeRate[];
  keys: KeyMapping[];
  repos: RepoMapping[];
  /** GitHub organisation or user whose Actions billing is pulled. */
  githubOwner: string;
  githubOwnerKind: 'org' | 'user';
  accounts: AccountMap;
  /** Accounts payable / bank account the journal credits. */
  creditAccount: string;
  /** |variance| above this fraction is flagged (default 0.02, the spec's success measure). */
  varianceThreshold: number;
}

/** PUT /api/finance/settings: any subset, plus write-only credentials ('' clears one). */
export type FinanceSettingsInput = Partial<FinanceSettings> & { anthropicAdminKey?: string; githubToken?: string };

export type SyncKind = 'billing' | 'github';

export interface SyncStatus {
  kind: SyncKind;
  /** A credential is present (from settings or the environment). */
  configured: boolean;
  /** Where the credential comes from, never the secret: 'settings' or the env var name. */
  credentialFrom: string | null;
  /** Last four characters of the credential, for recognition. */
  hint: string | null;
  lastRunAt: Ts | null;
  lastOkAt: Ts | null;
  running: boolean;
  /** The last run failed: what, why, next. */
  error: { what: string; why: string; next: string } | null;
  /** The last run worked but left some of it out (`error` says which part). */
  partial?: boolean;
  /** First and last day (UTC for billing) the stored data covers. */
  coversFrom: string | null;
  coversTo: string | null;
  nextRunAt: Ts | null;
}

/**
 * One API key's billed tokens on one UTC day and model, from the Anthropic usage report
 * (`group_by[]=api_key_id`). Never a key value: the report names keys by id only.
 */
export interface ProviderKeyUsage {
  /** UTC day. */
  day: string;
  model: string | null;
  keyId: string;
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite5m: number;
  cacheWrite1h: number;
}

/** What the finance module offers other modules: ctx.ext<FinanceApi>('finance'). */
export interface FinanceApi {
  /**
   * API-key usage from the provider report (ADR 0067, amended 19 Oct 2026). `connected`: an
   * Admin key is set. `names`: a key's label (Finance settings, else its name in the Admin API).
   * `version` changes whenever the report data does.
   */
  providerKeys?(): { connected: boolean; usage: ProviderKeyUsage[]; names: Record<string, string>; machines: Record<string, string[]>; version: number };
}

/** state.ext.finance: small and live; the heavy report is GET /api/finance/report. */
export interface FinanceState {
  settings: FinanceSettings;
  entries: CostEntry[];
  subscriptions: Subscription[];
  sync: Record<SyncKind, SyncStatus>;
  /** Bumped whenever anything a report depends on changes (entries, a pull, settings). */
  version: number;
  updatedAt: Ts;
}

// ---------------------------------------------------------------------------
// Report (GET /api/finance/report?from=&to=&project=)
// ---------------------------------------------------------------------------

export type CategoryAmounts = Record<CostCategory, number>;

export interface PnlWeek {
  /** Monday 'YYYY-MM-DD' (local). */
  week: string;
  byCategory: CategoryAmounts;
  total: number;
  /** Merged pull requests that week (landed queue items count when GitHub is not set up). */
  merged: number;
}

export interface PnlProject {
  /** '' for spend with no project. */
  projectId: Id;
  label: string;
  byCategory: CategoryAmounts;
  total: number;
}

export interface MissionCost {
  missionId: Id;
  title: string;
  state: string | null;
  cost: number;
  sessions: number;
}

export interface Kpis {
  /** Every category, and tokens alone, over the period. */
  total: number;
  tokens: number;
  mergedPrs: number;
  costPerMergedPr: number | null;
  tokenCostPerMergedPr: number | null;
  /** Hours sessions were working, sampled while the hub ran. */
  activeHours: number;
  costPerActiveHour: number | null;
  missions: MissionCost[];
  costPerMission: number | null;
  /** cacheRead / (input + cacheRead + cacheWrite). */
  cacheHitRatio: number | null;
  /** USD the cache saved: cache reads at the input price minus what they cost. */
  cacheSavings: number;
  /** Cost of PRs reverted within 7 days or closed unmerged, over cost attributed to PRs. */
  reworkRate: number | null;
  reworkCost: number;
  attributedPrCost: number;
  /** Time sessions sat waiting on a person, sampled while the hub ran. */
  approvalWait: { hours: number; episodes: number; medianMinutes: number | null; p90Minutes: number | null };
  /** Hours the hub sampled in the period (to judge active and waiting time). */
  sampledHours: number;
}

export interface PrCost {
  projectId: Id;
  repo: string;
  number: number;
  title: string;
  state: 'merged' | 'abandoned' | 'reverted' | 'open';
  mergedAt: Ts | null;
  closedAt: Ts | null;
  branch: string;
  cost: number;
  sessions: number;
}

export interface VarianceRow {
  /** A day ('YYYY-MM-DD', UTC), a model id, or a key id. */
  key: string;
  label: string;
  telemetry: number;
  billing: number;
  /** telemetry - billing. */
  variance: number;
  /** variance / billing, null when billing is 0. */
  variancePct: number | null;
  flagged: boolean;
  /** Key rows: tokens on the bill (all kinds). */
  billingTokens?: number;
  /** Key rows: no machine is mapped to this key, so telemetry is unknown. */
  unmapped?: boolean;
  /** Day rows: no billing data for this day yet, so it is left out of the totals. */
  missing?: boolean;
}

export interface Reconciliation {
  /** 'ready': billing data covers the period; 'none': never pulled; 'subscription': nothing to reconcile. */
  status: 'ready' | 'none' | 'subscription';
  byDay: VarianceRow[];
  byModel: VarianceRow[];
  byKey: VarianceRow[];
  telemetry: number;
  /** Token charges on the bill (what telemetry can match). */
  billing: number;
  /** Other charges on the bill (web search, code execution, managed sessions), not in telemetry. */
  billingOther: number;
  variance: number;
  variancePct: number | null;
  threshold: number;
  /** Days in the period with no billing data yet (the report lags by a few minutes to a day). */
  missingDays: string[];
}

export interface ModelMixRow {
  /** Model id without variant. */
  model: string;
  label: string;
  cost: number;
  tokens: { input: number; output: number; cacheRead: number; cacheWrite: number; cacheWrite1h: number };
  /** USD per million tokens, from the current price list (null when unpriced). */
  rates: { input: number; output: number; cacheRead: number; cacheWrite: number; cacheWrite1h: number } | null;
  /** Share of this model's cost in light sessions (short answers, few calls): candidates to route cheaper. */
  lightShare: number;
}

export interface UnallocatedIssue {
  category: CostCategory;
  amount: number;
  why: string;
}

export interface FinanceReport {
  from: string;
  to: string;
  projectId: Id | null;
  /**
   * The account filter (ADR 0067): a SpendAccount key, UNKNOWN_ACCOUNT, or null for all. With an
   * account, the report covers that account's token spend only: other costs belong to no account.
   */
  accountKey?: string | null;
  /** Token spend in the period by account, most expensive first (ADR 0067). Older hubs omit it. */
  accounts?: AccountBucket[];
  billing: 'subscription' | 'api';
  pricingVersion: string;
  weeks: PnlWeek[];
  projects: PnlProject[];
  byCategory: CategoryAmounts;
  total: number;
  kpis: Kpis;
  prs: PrCost[];
  reconciliation: Reconciliation;
  modelMix: ModelMixRow[];
  /** Spend nothing could attribute to a project (a bug to fix, per the spec). */
  unallocated: UnallocatedIssue[];
  generatedAt: Ts;
}

/** SSE 'ext' event types from the finance module. */
export type FinanceEventType = 'state';

export function zeroCategories(): CategoryAmounts {
  return { tokens: 0, seats: 0, compute: 0, ci: 0, platform: 0, other: 0 };
}
